import { HttpClient, HttpParams } from '@angular/common/http';
import {
  EMPTY,
  firstValueFrom,
  lastValueFrom,
  Observable,
  Subject,
  take,
  of,
  BehaviorSubject,
} from 'rxjs';
import { catchError, map, shareReplay, timeout } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Injectable, Injector } from '@angular/core';
import {
  Address,
  AddressCreateDto,
  FileEntity,
  Referral,
  UserPasswordChangeResponse,
  UserReport,
  UserSchema,
  UserServiceEvent,
  UserServiceEventStatusNames,
  UserSetting,
  newUserModel,
} from '../models';
import {
  AppNotification,
  AuthStatusObject,
  Order,
  PaginationResult,
  UserDashboardReportInfo,
} from 'src/app/shared';
import { AuthService } from './auth.service';
import { UserSocket } from '../sockets/user.socket';
import { ConfigService, urls } from './config.service';
import {
  HTTP_REQUESTS_TIMEOUT,
  PaymentStatus,
  XeroxMessages,
} from '../constant';
import { PaymentService } from 'src/app/feature/payment/services';
import { resolve } from 'path';
import { reject } from 'lodash';
import { PrintSizes } from 'src/app/core';

@Injectable({ providedIn: 'root' })
export class UserService {
  // Private properties =====================================>

  private user!: UserSchema;
  private cacheRecord: Record<string, any> = {};
  public currentUser: BehaviorSubject<newUserModel> =
    new BehaviorSubject<newUserModel>({});
  private _status$: Subject<UserServiceEvent> = new Subject();
  private _notifications$: Subject<AppNotification[]> = new Subject();
  public userOrders: Subject<Order[]> = new Subject();
  private userStatus = new BehaviorSubject<{ status: 'active' | 'notActive' }>({
    status: 'notActive',
  }); // public userProfile: BehaviorSubject<UserSchema> = new BehaviorSubject({} as UserSchema);

  // Accessors =============================================>
  public activationBoxOpened: boolean = false;
  selectedPrintSize = PrintSizes.A4;

  public get notificationsSubject$(): Observable<AppNotification[]> {
    return this._notifications$.pipe(
      catchError(() => EMPTY),
      shareReplay(1)
    );
  }

  public get status$(): Observable<UserServiceEvent> {
    return this._status$.pipe(
      catchError(() => EMPTY),
      map((statusObject) => {
        this.throwUserUpdateEventOnBus(statusObject.payload);
        return statusObject;
      })
    );
  }

  // Constructor ===========================================>
  constructor(
    private readonly configService: ConfigService,
    private readonly authService: AuthService,
    private readonly httpClient: HttpClient,
    private readonly snackbar: MatSnackBar,
    private paymentService: PaymentService,
    private readonly injector: Injector
  ) {}

  /**
   * Gets the report of user itself to use in admin
   * panel.
   */
  public getUserReport(id: number): Observable<UserReport> {
    const url = this.configService
      .getUrl('user.report')
      .replace(':id', id.toString());

    return this.httpClient
      .get<UserReport>(url, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  public getUserSelfReport(): Observable<UserReport> {
    const url = this.configService.getUrl('user.selfReport');
    return this.httpClient
      .get<UserReport>(url, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  /**
   * Get User scores using id
   * only admins
   */

  public getUserScoresById(
    userId: number,
    filters: {
      page: number;
      limit: number;
    } = {
      page: 1,
      limit: 10,
    }
  ) {
    const url = this.configService
      .getUrl('user.scores')
      .replace(':id', userId.toString());
    const params = this.getMergedQueryParam(url, filters);
    return this.httpClient
      .get<PaginationResult<FileEntity>>(url, {
        params,
        withCredentials: true,
      })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  public getAdminUserScoresById(
    userId: number,
    filters: {
      page: number;
      limit: number;
    } = {
      page: 1,
      limit: 10,
    }
  ) {
    const url = this.configService
      .getUrl('score.userScore')
      .replace(':id', userId.toString());
    const params = this.getMergedQueryParam(url, filters);
    return this.httpClient
      .get<PaginationResult<FileEntity>>(url, {
        params,
        withCredentials: true,
      })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  getDiscountsByUserId(userId: string | number) {
    const url = urls.user.getUserDiscounts.replace(':id', userId.toString());
  // const params = this.getMergedQueryParam(url);
    return this.httpClient.get(url, { withCredentials: true}).pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  generateDiscountCode(request: any) {
    const url = urls.admin.generateDiscount;
    return this.httpClient.post(url, request, { withCredentials: true}).pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  public changeUserActivation(userId: number) {
    const url = urls.user.changeActivation.replace(':id', userId?.toString());

    return this.httpClient
      .put(url, null, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  public changeUserRole(userId: number, roleCode: number) {
    const url = urls.user.changeUserRole.replace(':id', userId?.toString());

    return this.httpClient
      .put(url, { roleCode }, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  /**
   * get specific user shared documents
   */
  public getUserSharedFiles(
    userId: number,
    filters: {
      page: number;
      limit: number;
      confirmed?: 'confirmed' | 'unconfirmed';
      search?: string;
    }
  ) {
    const url = this.configService
      .getUrl('user.documents')
      .replace(':id', userId.toString());
    const params = this.getMergedQueryParam(url, filters);

    return this.httpClient
      .get<PaginationResult<FileEntity>>(url, {
        params,
        withCredentials: true,
      })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  public getUsersList(options: {
    page?: number;
    limit?: number;
    search?: string;
    order?: 'ASC' | 'DESC';
    orderTarget?: 'id' | 'totalValue' | 'totalSharedFile';
    startDate?: any;
    endDate?: any;
  }): Observable<PaginationResult<UserSchema>> {
    const url = this.configService.getUrl('user.index');
    const params = this.getMergedQueryParam(url, options);

    return this.httpClient
      .get<PaginationResult<UserSchema>>(url, {
        withCredentials: true,
        params,
      })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  public async isLoggedIn() {
    try {
      const isLoggedIn = await this.authService.isLoggedIn();
      return isLoggedIn;
    } catch (e) {
      return false;
    }
  }

  public async isGoogleAuthorized(): Promise<boolean> {
    try {
      const user = await this.getUser();
      return !!user?.googleId;
    } catch (e) {
      return false;
    }
  }

  public createUserAddress(address: AddressCreateDto): Observable<Address> {
    const url = this.getUrl('address');
    return this.httpClient
      .post<Address>(url, address, {
        withCredentials: true,
      })

      .pipe(
        map((address) => {
          return address;
        }),
        timeout(HTTP_REQUESTS_TIMEOUT)
      );
  }

  public updateUserAddress(address: AddressCreateDto): Observable<Address> {
    const url = this.getUrl('address') + '/' + address.id;
    return this.httpClient
      .put<Address>(url, address, {
        withCredentials: true,
      })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }
  public deleteUserAddress(addressId: number): Observable<Address> {
    const url = this.getUrl('address') + '/' + addressId;
    return this.httpClient
      .delete<Address>(url, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  public getAddress(id: number): Observable<Address> {
    const url = this.getUrl('address') + '/' + id;
    // return this.httpClient.get<Address[]>(url, { withCredentials: true })
    var obj: Address = {
      id: 6,
      address: 'string11',
      city: 'string11',
      province: 'string11',
      postalCode: 'string11stri',
      phoneNumber: 'string11',
      landlinePhone: 'string11',
      recipientName: 'string11',
      houseNumber: '',
    };
    return of(obj);
  }

  public getUserAddresses(): Observable<Address[]> {
    const url = this.getUrl('address');
    return this.httpClient
      .get<Address[]>(url, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  async getUser(force?: boolean): Promise<UserSchema> {
    return new Promise((resolve, reject) => {
      this.authService.userProfile.subscribe({
        next: (data) => {
          return resolve(data as UserSchema);
        },
        error: (error) => {
          return reject(error);
        },
      });
    });
    // return await firstValueFrom(this.authService.getUserProfile());
  }

  async getUserSetting(): Promise<UserSetting | null> {
    try {
      const url = this.getUrl('setting');

      const userSetting = await firstValueFrom(
        this.httpClient
          .get(url, { withCredentials: true })
          .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
      );
      return userSetting as UserSetting;
    } catch (userSettingGetError) {
      console.error(userSettingGetError, 'returning null');
      return null;
    }
  }

  public getReferralObject() {
    const referralUrl = this.configService.getUrl('user.referral');

    return this.httpClient
      .get(referralUrl, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  public getReferreds() {
    const url = urls.user.getReferreds;
    return this.httpClient
    .get(url, { withCredentials: true })
    .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  public getUserShoppingCartOrders(): Observable<Order[]> {
    const url = this.getUrl('shoppingCart');

    // return this.httpClient
    //   .get<Order[]>(url, { withCredentials: true })
    //   .pipe(take(1), timeout(HTTP_REQUESTS_TIMEOUT));

    return new Observable((subscibe) => {
      this.httpClient
        .get<Order[]>(url, { withCredentials: true })
        .pipe(take(1), timeout(HTTP_REQUESTS_TIMEOUT))
        .subscribe(
          (orders: Order[]) => {
            this.userOrders.next(orders);
            subscibe.next(orders);
          },
          (error) => {
            this.userOrders.error([]);
            subscibe.error(error?.error);
          }
        );
    });
  }

  public updateUser(
    userInfoToUpdate: Pick<
      UserSchema,
      'fullName' | 'nationalCode' | 'birthDate' | 'email'
    >
  ): Observable<UserSchema> {
    const url = this.getUrl('update');

    return new Observable((subscriber) => {
      this.httpClient
        .put(url, userInfoToUpdate, { withCredentials: true })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
        .subscribe(
          (updateResult) => {
            this.getUser(true)
              .then((user: UserSchema) => {
                subscriber.next(user);
                this._status$.next(this.generateEvent('update', user));
              })
              .catch((e) => {});
          },

          (error) => {
            this.openDefaultSnackbar(
              XeroxMessages.USER_UPDATE.failure.NOT_UPDATED
            );
            subscriber.error(error?.error || error);
          }
        );
    });
  }

  public updateUserPhone(phone: string) {
    const url = this.getUrl('updatePhone');

    const withCredentials = true;
    return new Observable((subscriber) => {
      this.httpClient
        .put(url, { phone }, { withCredentials })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
        .subscribe(
          (result) => {
            subscriber.next(result);
            this.refreshUser(undefined, true);
          },
          (error) => {
            subscriber.next(error);
          }
        );
    });
  }

  public async isUserActivated(forced?: boolean): Promise<boolean> {
    try {
      if (this.cacheGet<boolean>('isUserActivated') && !forced) {
        return this.cacheRecord['isUserActivated'];
      }

      const url = this.configService.getUrl('activation.status');
      const result = await firstValueFrom(
        this.httpClient
          .get<{ status: 'active' | 'notActive' }>(url, {
            withCredentials: true,
          })
          .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
      );

      const isActivatedBoolean = result?.status === 'active' ? true : false;

      this.cache('isUserActivated', isActivatedBoolean);
      return isActivatedBoolean;
    } catch (e) {
      return false;
    }
  }

  public logoutUser(): Observable<AuthStatusObject> {
    return this.authService.logUserOut();
  }

  public refreshUser(user?: UserSchema, force?: boolean) {
    if (user) {
      this._status$.next(this.generateEvent('update', user));
    }

    this.getUser(force)
      .then((user) => {
        this._status$.next(this.generateEvent('update', user));
      })
      .catch((error) => {
        console.error(error);
        this._status$.error(error);
      });
  }

  changeUserPassword(changePasswordData: {
    currentPassword: string;
    newPassword: string;
  }): Observable<UserPasswordChangeResponse> {
    const url = this.getUrl('changePassword');

    return new Observable((subscriber) => {
      this.httpClient
        .post(url, changePasswordData, { withCredentials: true })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
        .subscribe(
          (response) => {
            const passChangeObject = response as UserPasswordChangeResponse;
            if (!passChangeObject.updated) {
              subscriber.error(passChangeObject);
            } else {
              subscriber.next(passChangeObject);
            }
          },

          (error) => {
            subscriber.error(error);
          }
        );
    });
  }

  activateUser(codeGenerateBody: {
    code: number;
    reason: string;
  }): Observable<any> {
    const url = this.configService.getUrl('activation.activate');

    return new Observable((subscriber) => {
      this.httpClient
        .post(url, codeGenerateBody, { withCredentials: true })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
        .subscribe(
          (data) => {
            subscriber.next(data);
            this._status$.next(this.generateEvent('activate', this.user));
          },
          (error) => {
            subscriber.error(error);
          }
        );
    });
  }

  generateActivationCode(codeGenerateBody: {
    phone: string;
    reason: string;
  }): Observable<number | undefined> {
    const codeGenerateUrl = this.configService.getUrl(
      'activation.code.generate'
    );
    const url = codeGenerateUrl + codeGenerateBody.reason;

    return new Observable((subscriber) => {
      this.httpClient

        .post(url, codeGenerateBody, { withCredentials: true })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
        .subscribe({
          next: () => subscriber.next(),
          error: (err) => subscriber.error(err),
        });
    });
  }

  getUserNotifications(): Observable<AppNotification[]> {
    const url = this.getUrl('notifications');
    return this.httpClient
      .get<AppNotification[]>(url, { withCredentials: true })
      .pipe(
        map((value) => {
          this._notifications$.next(value);
          return value;
        }),
        timeout(HTTP_REQUESTS_TIMEOUT),
        catchError((error) => {
          return [];
        })
      );
  }

  /**
   * Generate Dashboard info
   * @param {Order[]} payments (optional) If you do not pass we get it from server
   */
  public async generateDashboardReportData(
    orders?: Order[]
  ): Promise<UserDashboardReportInfo> {
    const payments = await firstValueFrom(
      this.paymentService.getUserPayments()
    );
    return {
      totalCalculation: 0,
      totalDelivered: payments.items.filter(
        (item) => item.status === PaymentStatus.Sent
      ).length,
      totalProgress: payments.items!.filter(
        (item) => item.status === PaymentStatus.Implementation
      ).length,
      orders: [],
    };
  }

  // Private methods ------------------------------------>

  private getMergedQueryParam(
    url: string,
    objectOfParams: Record<string, any>
  ): HttpParams {
    for (const key in objectOfParams) {
      if (!!!objectOfParams[key]) {
        delete objectOfParams[key];
      }
    }

    const params = new HttpParams({ fromObject: objectOfParams });
    return params;
  }

  private openDefaultSnackbar(msg: string, duration?: number) {
    this.snackbar.open(msg, 'باشه !', { duration: duration || 1000 });
  }

  private cache(key: string, data: any) {
    this.cacheRecord[key] = data;
  }

  private cacheGet<T>(key: string): T | undefined {
    return this.cacheRecord[key];
  }

  private mapStatus(statusObject: AuthStatusObject): AuthStatusObject {
    const type = statusObject?.type;

    if (type === 'google-auth' || type === 'login') {
      this.isLoggedIn().then((isLoggedIn) => {
        this.isUserActivated().then((userActivated) => {
          if (isLoggedIn && !userActivated && !this.activationBoxOpened) {
            this.authService.openUserActivationBoxDelay(3000);
          }
        });
      });
    }

    return statusObject;
  }

  private throwUserUpdateEventOnBus(payload?: UserSchema) {
    // if (this.userBusListener) {
    //   if (payload) {
    //     this.userBusListener.throw(payload);
    //   } else {
    //     this.getUser()
    //       .then((user) => this.userBusListener.throw(user))
    //       .catch((e) => {});
    //   }
    // }
  }

  private generateEvent(
    status: UserServiceEventStatusNames,
    user?: UserSchema
  ): UserServiceEvent {
    return { payload: user, status: status };
  }

  private getUrl(target: string): string {
    return this.configService.getUrl(`user.${target}`);
  }
}
